import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShoppingCart, Download, Check, Car, PackageCheck, History, PackagePlus, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [o, h] = await Promise.all([api.get("/orders"), api.get("/orders/history")]);
    setOrders(o.data);
    setHistory(h.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const mark = async (o) => {
    await api.post(`/repairs/${o.repair_id}/order-piece/${o.idx}/mark`);
    toast.success("Pièce marquée commandée");
    load();
  };

  const cancelHistory = async (hid) => {
    await api.post(`/orders/history/${hid}/cancel`);
    toast.success("Commande annulée — pièce remise à « à commander »");
    load();
  };

  const toStock = async (h) => {
    await api.post(`/orders/history/${h.id}/to-stock`);
    toast.success(`« ${h.designation} » ajouté au stock (×${h.quantite})`);
    load();
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/orders/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "bon-de-commande.pdf"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Bon de commande téléchargé");
    } catch (e) { toast.error("Erreur PDF"); }
  };

  const totalRefs = orders.length;
  const totalUnits = orders.reduce((a, o) => a + (o.quantite || 0), 0);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC]">Pièces à commander</h1>
          <p className="text-[#94A3B8] text-sm">Chaque pièce à commander est listée séparément par véhicule (non regroupée).</p>
        </div>
        <Button data-testid="orders-pdf-button" onClick={downloadPdf} disabled={orders.length === 0} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <Download className="w-4 h-4 mr-2" /> Bon de commande PDF
        </Button>
      </div>

      <Tabs defaultValue="a-commander" className="w-full">
        <TabsList className="bg-[#121824] border border-[#2A364F]">
          <TabsTrigger value="a-commander" data-testid="tab-a-commander" className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white text-[#94A3B8]">À commander</TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-historique" className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white text-[#94A3B8]">Historique ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="a-commander" className="mt-4 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-5">
              <div className="font-heading text-3xl font-extrabold text-[#F8FAFC]">{totalRefs}</div>
              <div className="text-sm text-[#94A3B8] mt-1">Pièces à commander</div>
            </div>
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-5">
              <div className="font-heading text-3xl font-extrabold text-[#F59E0B]">{totalUnits}</div>
              <div className="text-sm text-[#94A3B8] mt-1">Unités au total</div>
            </div>
          </div>

          {loading ? (
            <div className="text-[#94A3B8]">Chargement…</div>
          ) : orders.length === 0 ? (
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-10 text-center">
              <PackageCheck className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <div className="text-[#F8FAFC] font-semibold">Aucune pièce à commander</div>
              <div className="text-sm text-[#64748B] mt-1">Les pièces « à commander » saisies dans les fiches apparaîtront ici.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={`${o.repair_id}-${o.idx}`} data-testid={`order-${o.repair_id}-${o.idx}`} className="bg-[#182232] border border-[#2A364F] rounded-xl p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0"><ShoppingCart className="w-6 h-6" /></div>
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-[#F8FAFC] truncate">{o.designation}</div>
                        <div className="text-xs text-[#64748B] font-mono">{o.reference || "réf. —"}</div>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          {o.vehicle?.immatriculation
                            ? <span className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-1.5 py-0.5 rounded border border-yellow-500/30 uppercase text-[10px]">{o.vehicle.immatriculation}</span>
                            : <span className="text-[#EF4444] text-xs font-semibold">Immat. manquante</span>}
                          <span className="text-xs text-[#94A3B8]">{o.vehicle ? `${o.vehicle.marque || ""} ${o.vehicle.modele || ""}`.trim() : ""}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="font-mono font-bold text-[#F59E0B] text-lg">×{o.quantite}</div>
                      <Button data-testid={`mark-order-${o.repair_id}-${o.idx}`} onClick={() => mark(o)} className="bg-[#10B981] hover:bg-[#059669] text-white">
                        <Check className="w-4 h-4 mr-1" /> Commandé
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historique" className="mt-4">
          {history.length === 0 ? (
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-10 text-center">
              <History className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
              <div className="text-[#F8FAFC] font-semibold">Aucune commande passée</div>
              <div className="text-sm text-[#64748B] mt-1">Les pièces marquées « commandées » apparaîtront ici avec leur date.</div>
            </div>
          ) : (
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="orders-history-table">
                <thead>
                  <tr className="text-[#64748B] text-xs uppercase tracking-wider border-b border-[#2A364F]">
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Désignation</th>
                    <th className="text-left px-4 py-3">Réf.</th>
                    <th className="text-left px-4 py-3">Véhicule(s)</th>
                    <th className="text-right px-4 py-3">Qté</th>
                    <th className="text-left px-4 py-3">Par</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2A364F]">
                  {history.map((h) => (
                    <tr key={h.id} data-testid={`history-${h.id}`} className="hover:bg-[#1E2A3E]">
                      <td className="px-4 py-3 font-mono text-[#94A3B8]">{(h.date || "").slice(0, 10)} <span className="text-[#64748B]">{(h.date || "").slice(11, 16)}</span></td>
                      <td className="px-4 py-3 text-[#F8FAFC]">{h.designation}{h.stocked && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-400 border-emerald-500/30">En stock</span>}</td>
                      <td className="px-4 py-3 font-mono text-xs text-cyan-400">{h.reference || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(h.immatriculations || "").split(",").map((p) => p.trim()).filter(Boolean).map((p, i) => (
                            <span key={i} className="font-mono font-bold tracking-widest text-yellow-400 bg-slate-900 px-1.5 py-0.5 rounded border border-yellow-500/30 uppercase text-[10px]">{p}</span>
                          ))}
                          {!(h.immatriculations || "").trim() && <span className="text-xs text-[#64748B]">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[#F59E0B]">×{h.quantite}</td>
                      <td className="px-4 py-3 text-[#94A3B8]">{h.marked_by || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button data-testid={`to-stock-${h.id}`} onClick={() => toStock(h)} size="sm" variant="outline" className="h-8 border-[#2A364F] bg-transparent text-cyan-400 hover:text-cyan-300"><PackagePlus className="w-3.5 h-3.5 mr-1" />Ajouter au stock</Button>
                          <Button data-testid={`cancel-history-${h.id}`} onClick={() => cancelHistory(h.id)} size="sm" variant="outline" className="h-8 border-[#2A364F] bg-transparent text-[#94A3B8] hover:text-[#EF4444]"><RotateCcw className="w-3.5 h-3.5 mr-1" />Annuler</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
